import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, X, Check, Mail, ChevronRight, Clock } from 'lucide-react';
import { getFriends, getFriendRequests, getPendingRequests, sendFriendRequest, acceptFriendRequest, rejectFriendRequest, removeFriend } from '../api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';

export default function Friends() {
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const toast = useToast();
  const confirm = useConfirm();

  const load = useCallback(() => {
    Promise.all([getFriends(), getFriendRequests(), getPendingRequests()])
      .then(([f, r, p]) => {
        setFriends(f.data);
        setRequests(r.data);
        setPending(p.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  async function handleSendRequest(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await sendFriendRequest(email.trim());
      toast.success('Friend request sent!');
      setEmail('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send request');
    } finally {
      setSending(false);
    }
  }

  async function handleAccept(id) {
    setAcceptingId(id);
    try {
      await acceptFriendRequest(id);
      toast.success('Friend request accepted!');
      load();
    } catch {
      toast.error('Failed to accept request');
    } finally {
      setAcceptingId(null);
    }
  }

  async function handleReject(id) {
    setRejectingId(id);
    try {
      await rejectFriendRequest(id);
      load();
    } catch {
      toast.error('Failed to reject request');
    } finally {
      setRejectingId(null);
    }
  }

  async function handleRemove(friend) {
    const ok = await confirm(`Remove ${friend.username || friend.firstName} from your friends?`, { confirmLabel: 'Remove' });
    if (!ok) return;
    try {
      await removeFriend(friend.friendshipId);
      toast.success('Friend removed');
      load();
    } catch {
      toast.error('Failed to remove friend');
    }
  }

  return (
    <div className="fade-in">
      <div className="page-header__row" style={{ marginBottom: '28px' }}>
        <div>
          <h1 className="page-header__title">FRIENDS</h1>
          <p className="page-header__subtitle">
            {friends.length} friend{friends.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Add Friend */}
      <div className="card friends-add">
        <h2 className="friends-add__title">ADD FRIEND</h2>
        <form onSubmit={handleSendRequest} className="friends-add__form">
          <div className="friends-add__input-wrap">
            <Mail size={16} className="friends-add__icon" />
            <input
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Enter their email address..."
              type="email"
              className="friends-add__input"
            />
          </div>
          <button type="submit" className="btn btn--primary" disabled={sending || !email.trim()}>
            {sending ? <span className="spinner spinner--sm" /> : <><UserPlus size={14} /> SEND</>}
          </button>
        </form>
      </div>

      {/* Pending Incoming Requests */}
      {requests.length > 0 && (
        <div className="friends-section">
          <h2 className="section-header__title friends-section__title">
            FRIEND REQUESTS
            <span className="friends-section__badge">{requests.length}</span>
          </h2>
          <div className="grid grid--gap-md">
            {requests.map(req => (
              <div key={req._id} className="card friend-request">
                <div className="flex-1">
                  <div className="friend-request__name">
                    {req.from.username || `${req.from.firstName} ${req.from.lastName}`}
                  </div>
                  <div className="friend-request__email">{req.from.email}</div>
                </div>
                <div className="friend-request__actions">
                  <button
                    className="btn btn--primary btn--sm"
                    onClick={() => handleAccept(req._id)}
                    disabled={acceptingId === req._id}
                  >
                    {acceptingId === req._id ? <span className="spinner spinner--sm" /> : <><Check size={14} /> Accept</>}
                  </button>
                  <button
                    className="btn btn--secondary btn--sm"
                    onClick={() => handleReject(req._id)}
                    disabled={rejectingId === req._id}
                  >
                    {rejectingId === req._id ? <span className="spinner spinner--sm" /> : <X size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Outgoing Requests */}
      {pending.length > 0 && (
        <div className="friends-section">
          <h2 className="section-header__title friends-section__title">PENDING</h2>
          <div className="grid grid--gap-md">
            {pending.map(p => (
              <div key={p._id} className="card friend-pending">
                <Clock size={14} className="text-muted" />
                <div className="flex-1">
                  <span className="friend-pending__name">
                    {p.to.username || `${p.to.firstName} ${p.to.lastName}`}
                  </span>
                  <span className="friend-pending__email">{p.to.email}</span>
                </div>
                <span className="pill">Pending</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends List */}
      {loading ? (
        <div className="loading"><div className="spinner spinner--lg" /></div>
      ) : friends.length === 0 && requests.length === 0 ? (
        <div className="empty-state">
          <p>No friends yet. Send a request using their email above!</p>
        </div>
      ) : friends.length > 0 && (
        <div className="friends-section">
          <h2 className="section-header__title friends-section__title">YOUR FRIENDS</h2>
          <div className="grid grid--gap-md">
            {friends.map(friend => (
              <div key={friend._id} className="card friend-card">
                <Link to={`/friends/${friend._id}`} className="friend-card__link">
                  <div className="friend-card__avatar">
                    {(friend.username || friend.firstName || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="friend-card__name">
                      {friend.username || `${friend.firstName} ${friend.lastName}`}
                    </div>
                    {friend.username && (
                      <div className="friend-card__realname">{friend.firstName} {friend.lastName}</div>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-muted" />
                </Link>
                <button
                  className="btn btn--danger btn--sm friend-card__remove"
                  aria-label={`Remove ${friend.firstName}`}
                  onClick={() => handleRemove(friend)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
